import * as React from "react";
import { useState, useMemo } from "react";
import styled from "styled-components";
import { JSONStringify } from "json-with-bigint";
import useProductName from "brand-customization/useProductName";
import { useErrorsContext } from "components/lookup-tables/contexts/ErrorsContext";
import { Col, Row, DataWell } from "components/lookup-tables/layout-componets";
import { Button, Input, Alert } from "components/bootstrap";
import { useFetchLookupPreview, useTestLookupTableKey } from "components/lookup-tables/hooks/useLookupTablesAPI";
import { Description } from "./lookup-table-show";
const StyledDataWell = styled(DataWell)`
  line-height: normal;
  white-space: pre;
  font-family: monospace;
  font-size: medium;
  color: ${({ theme }) => theme.mode === "light" ? "darkslateblue" : "lightsteelblue"};
  overflow: auto;
  max-height: 200px;
`;
const NoMarginInput = styled.div`
  & .form-group {
    margin-bottom: 0;
  }
`;
const StyledAlert = styled(Alert)`
  width: 100%;
`;
const INIT_INPUT = { value: "", valid: false };
function TestLookup({ table }) {
  const { errors } = useErrorsContext();
  const lutError = errors == null ? void 0 : errors.lutErrors[table.name];
  const [lookupKey, setLookupKey] = useState(INIT_INPUT);
  const [lookupResult, setLookupResult] = useState(null);
  const [previewSize, setPreviewSize] = useState(5);
  const productName = useProductName();
  const { testLookupTableKey } = useTestLookupTableKey();
  const {
    lookupPreview: { results, total, supported }
  } = useFetchLookupPreview(table.id, !lutError, previewSize);
  const dataPreview = useMemo(() => {
    if (total === 0) return "No results to show";
    return JSONStringify(results, null, 2);
  }, [results, total]);
  const onChange = (event) => {
    const newValue = { ...lookupKey };
    newValue.valid = event.target.value && event.target.value.replace(/\s/g, "").length > 0;
    newValue.value = event.target.value;
    setLookupKey(newValue);
    setLookupResult(null);
  };
  const onKeyDown = (e) => {
    if (e.key === "Backspace") setLookupResult(null);
  };
  const onReset = () => {
    setLookupKey(INIT_INPUT);
    setLookupResult(null);
  };
  const handleLookupKey = (event) => {
    event.preventDefault();
    if (lookupKey.valid) {
      testLookupTableKey({ tableName: table.name, key: lookupKey.value }).then((resp) => {
        setLookupResult(JSONStringify(resp, null, 2));
        setLookupKey({ value: "", valid: false });
      });
    }
  };
  const onPreviewSizeChange = ({ target: { value } }) => {
    const auxValue = value ? +value : value;
    if (typeof auxValue !== "number") {
      setPreviewSize(auxValue);
    } else if (auxValue < 1) {
      setPreviewSize(1);
    } else {
      setPreviewSize(auxValue);
    }
  };
  const showResults = useMemo(() => !lutError && (lookupResult || supported), [lutError, lookupResult, supported]);
  return /* @__PURE__ */ React.createElement(Col, { $gap: "sm" }, /* @__PURE__ */ React.createElement(Col, { $gap: "xs" }, /* @__PURE__ */ React.createElement("h2", null, "Test lookup"), /* @__PURE__ */ React.createElement(Description, null, "You can manually query the lookup table using this form. The data will be cached as configured by", " ", productName, ".")), /* @__PURE__ */ React.createElement(Col, { $gap: "xs" }, /* @__PURE__ */ React.createElement("form", { onSubmit: handleLookupKey, style: { width: "100%" } }, /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "text",
      id: "key",
      name: "lookupkey",
      placeholder: "Insert key that should be looked up",
      label: "Key",
      required: true,
      onKeyDown,
      onChange,
      help: "Key to look up a value for.",
      value: lookupKey.value
    }
  ), /* @__PURE__ */ React.createElement(Row, { $justify: "flex-end" }, /* @__PURE__ */ React.createElement(Button, { name: "reset", disabled: !lookupResult, onClick: onReset }, "Reset"), /* @__PURE__ */ React.createElement(
    Button,
    {
      type: "submit",
      name: "lookupbutton",
      bsStyle: "primary",
      "data-testid": "lookupbutton",
      disabled: !lookupKey.valid
    },
    "Look up"
  ))))), lutError && /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "danger" }, lutError), showResults && /* @__PURE__ */ React.createElement(Col, { $gap: "xs", style: { marginTop: 20 } }, /* @__PURE__ */ React.createElement("h4", { style: { width: "100%" } }, /* @__PURE__ */ React.createElement(Row, { $align: "center", $justify: "space-between" }, /* @__PURE__ */ React.createElement("span", null, "Lookup result"), supported && total > 0 && !lookupResult && /* @__PURE__ */ React.createElement(Row, { $width: "auto", $align: "center" }, /* @__PURE__ */ React.createElement(NoMarginInput, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "number",
      bsSize: "sm",
      onChange: onPreviewSizeChange,
      value: previewSize > total ? total : previewSize,
      style: { marginLeft: "auto" },
      min: 1,
      max: total
    }
  )), /* @__PURE__ */ React.createElement(Description, null, "of"), /* @__PURE__ */ React.createElement(Description, null, total)))), /* @__PURE__ */ React.createElement(StyledDataWell, null, lookupResult != null ? lookupResult : dataPreview)));
}
export default TestLookup;
